import { SIGNUP_PATHS } from '@src/constants/navigation'

import { PAYWALL_INTENTS, type PaywallIntentId } from '../intent'

/** `?prompt=` key the Conversion checkout registers in PromptHandler. */
export const CONVERSION_CHECKOUT_PROMPT = 'checkout'

/** Query params the checkout owns; cleared when it closes and replaced when it opens. */
export const CONVERSION_CHECKOUT_PARAMS = [
  'prompt',
  'productId',
  'termId',
  'couponCode',
  'intent',
  'tokens',
] as const

const MAX_TOKENS = 8
const MAX_TOKEN_LENGTH = 120

export type ConversionCheckoutRequest = {
  productId: string
  intent?: PaywallIntentId | null
  tokens?: Record<string, string> | null
  termId?: string | null
  couponCode?: string | null
}

function cleanTokens(tokens?: Record<string, unknown> | null): Record<string, string> | null {
  if (!tokens || typeof tokens !== 'object') {
    return null
  }
  const entries = Object.entries(tokens)
    .filter(([key, value]) => /^[a-zA-Z]\w{0,31}$/.test(key) && typeof value === 'string' && value.trim())
    .slice(0, MAX_TOKENS)
    .map(([key, value]) => [key, (value as string).trim().slice(0, MAX_TOKEN_LENGTH)] as const)
  return entries.length ? Object.fromEntries(entries) : null
}

/**
 * Link that opens the Conversion checkout over the current page. Keeps the page's own query
 * (filters, selected thread, ...) so closing the dialog lands exactly where the founder was.
 */
export function conversionCheckoutHref(
  pathname: string,
  currentSearch: string | URLSearchParams | null | undefined,
  request: ConversionCheckoutRequest
): string {
  const params = new URLSearchParams(currentSearch ? currentSearch.toString() : '')
  for (const key of CONVERSION_CHECKOUT_PARAMS) {
    params.delete(key)
  }
  params.set('prompt', CONVERSION_CHECKOUT_PROMPT)
  params.set('productId', request.productId)
  if (request.intent && request.intent !== 'default') {
    params.set('intent', request.intent)
  }
  const tokens = cleanTokens(request.tokens)
  if (tokens) {
    params.set('tokens', JSON.stringify(tokens))
  }
  if (request.termId) {
    params.set('termId', request.termId)
  }
  if (request.couponCode) {
    params.set('couponCode', request.couponCode)
  }
  return `${pathname || '/'}?${params.toString()}`
}

type PaymentProductLink = {
  productId?: string | null
  termId?: string | null
  effectiveTermId?: string | null
  appliedCoupon?: { code?: string | null } | null
}

/**
 * Standalone payment page for a plan card. New-tab and copy-link use this; an unmodified click
 * still opens the checkout dialog on the current page.
 */
export function acceleratorPaymentHref(product?: PaymentProductLink | null): string | null {
  const productId = product?.productId?.trim()
  if (!productId) {
    return null
  }
  const params = new URLSearchParams()
  params.set('productId', productId)
  const termId = product?.effectiveTermId?.trim() || product?.termId?.trim()
  if (termId) {
    params.set('termId', termId)
  }
  const couponCode = product?.appliedCoupon?.code?.trim()
  if (couponCode) {
    params.set('couponCode', couponCode)
  }
  return `${SIGNUP_PATHS.payment}?${params.toString()}`
}

type ParamReader = { get: (key: string) => string | null }

/** Parses the checkout params defensively: unknown intents and malformed tokens are dropped. */
export function readConversionCheckoutParams(params: ParamReader | null | undefined) {
  const productId = params?.get('productId')?.trim() || null
  const intentParam = params?.get('intent')?.trim() || null
  const intent =
    intentParam && Object.prototype.hasOwnProperty.call(PAYWALL_INTENTS, intentParam)
      ? (intentParam as PaywallIntentId)
      : null
  let tokens: Record<string, string> | null = null
  const rawTokens = params?.get('tokens')
  if (rawTokens) {
    try {
      tokens = cleanTokens(JSON.parse(rawTokens))
    } catch {
      tokens = null
    }
  }
  return {
    productId,
    intent,
    tokens,
    termId: params?.get('termId')?.trim() || null,
    couponCode: params?.get('couponCode')?.trim() || null,
  }
}

/**
 * One coupon precedence for every Conversion surface, matching `/accelerator/payment`: explicit
 * URL codes, then the member's default code, then the `sco_promo` cookie. The API decides whether
 * it applies (`appliedCoupon`); this only picks which code to ask about.
 */
export function resolveCheckoutCouponCode({
  params,
  defaultCouponCode,
  promoCookie,
}: {
  params?: ParamReader | null
  defaultCouponCode?: string | null
  promoCookie?: string | null
}): string | null {
  const candidates = [
    params?.get('couponCode'),
    params?.get('promo'),
    params?.get('fgCouponCode'),
    params?.get('facCouponCode'),
    defaultCouponCode,
    promoCookie,
  ]
  return candidates.map(code => code?.trim()).find(Boolean) || null
}
